import { Component, Input, OnInit } from '@angular/core';
import { SearchResultModel } from '@app/search/components/model/search-result.model';
import { AddressPipe } from '@app/common/pipes/address.pipe';
import { SearchResultSubjectBirthYearPipe } from '@app/search/components/pipes/search-result-subject-birth-year.pipe';
import { CATEGORY_CONFIGS, CategoryConfig, FieldConfig } from './search-result-category.config';

@Component({
  templateUrl: './search-result.component.html',
  styleUrls: ['search-result.component.scss'],
  selector: 'search-result',

})
export class SearchResultComponent implements OnInit {

  @Input() result: SearchResultModel;
  @Input() type: 's' | 'l';
  data: any;
  config!: CategoryConfig;

  constructor(
    private addressPipe: AddressPipe,
    private birthYearPipe: SearchResultSubjectBirthYearPipe,
  ) {}

  ngOnInit() {
    this.data = this.result.data;
    this.config = CATEGORY_CONFIGS[this.result.type];

    const d = this.data;
    if (this.result.type === 'subject') {
      this.prepareSubjectData(d);
    }
    if (this.result.type === 'parcel') {
      d._parcelName = (d.budouci ? d.budouci + ' ' : '') + d.parcisNumber;
    }
  }

  /** Vrátí hodnotu fieldu z data objektu. */
  resolveValue(field: string): any {
    return this.data ? this.data[field] : '';
  }

  /** Zkontroluje, zda se má field zobrazit. */
  isFieldVisible(field: FieldConfig): boolean {
    if (field.condition === true) { return true; }
    if (field.condition) { return this.checkCondition(field.condition); }
    // Default: zobraz jen pokud data[field] je truthy
    const val = this.data ? this.data[field.field] : null;
    return val != null && val !== '';
  }

  /** Vyhodnotí podmínku (section-level nebo explicitní field condition). */
  checkCondition(condition: string | string[]): boolean {
    if (!condition) { return true; }
    if (Array.isArray(condition)) {
      return condition.every(c => this.evalSingle(c));
    }
    return this.evalSingle(condition);
  }

  /** Přeloží params mapu na konkrétní hodnoty z data objektu. */
  resolveParams(paramsMap: { [key: string]: string }, source?: any): any {
    const src = source || this.data;
    const result: any = {};
    Object.keys(paramsMap).forEach(function(key) {
      const val = paramsMap[key];
      if (val === '$null') {
        result[key] = null;
      } else if (val && val.charAt(0) === '$') {
        result[key] = val.substring(1);
      } else {
        result[key] = src ? src[val] : undefined;
      }
    });
    return result;
  }

  private static readonly SUBJECT_IGNORE_LIST = [
    'Česká republika',
  ];

  /** Rozdělí pipe-separated string na pole jmen pro subjectList. */
  resolveSubjectList(field: string): string[] {
    const val = this.data ? this.data[field] : null;
    if (!val) { return []; }
    return String(val)
      .split('|')
      .map(s => s.trim())
      .filter(s => s.length > 0 && !SearchResultComponent.SUBJECT_IGNORE_LIST.includes(s));
  }

  /** Pro mode=conditional vrátí první odpovídající link config. */
  getActiveProjectLink(): any {
    if (!this.config || !this.config.projectLink || this.config.projectLink.mode !== 'conditional') {
      return null;
    }
    const links = (this.config.projectLink as any).links;
    for (let i = 0; i < links.length; i++) {
      if (!links[i].condition || (this.data && !!this.data[links[i].condition])) {
        return links[i];
      }
    }
    return null;
  }

  onClick() {
    console.log('raw data', this.result);
  }

  // ---------------------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------------------

  private evalSingle(condition: string): boolean {
    const eqIdx = condition.indexOf('=');
    if (eqIdx !== -1) {
      const field = condition.substring(0, eqIdx);
      const value = condition.substring(eqIdx + 1);
      return this.data && String(this.data[field]) === value;
    }
    return this.data && !!this.data[condition];
  }

  private prepareSubjectData(d: any) {
    const addr1 = {
      ulice: d.street || null,
      cisloDomovni: d.houseNumber || d.registrationNumber || null,
      cisloOrientacni: d.streetNumber || null,
      cpCe: d.cpCe ? d.cpCe : 1,
      castObce: d.neighbourhood || null,
      mestskaCast: d.quarter || null,
      obec: d.municipality || null,
      psc: d.postCode || null,
    };
    const addr2 = {
      ulice: d.street2 || null,
      cisloDomovni: d.houseNumber2 || d.registrationNumber2 || null,
      cisloOrientacni: d.streetNumber2 || null,
      cpCe: d.cpCe2 ? d.cpCe2 : 1,
      castObce: d.neighbourhood2 || null,
      mestskaCast: d.quarter2 || null,
      obec: d.municipality2 || null,
      psc: d.postCode2 || null,
    };

    d._subjectName = this.buildSubjectName();
    d._person1Name = [d.surname, d.firstName].filter(Boolean).join(' ');
    d._person2Name = [d.surname2, d.firstName2].filter(Boolean).join(' ');
    d._address1 = this.addressPipe.transform(addr1);
    d._address2 = this.addressPipe.transform(addr2);
    if (d.personalNumber) {
      d._birthYear = this.birthYearPipe.transform(d.personalNumber);
    }
    if (d.personalNumber2) {
      d._birthYear2 = this.birthYearPipe.transform(d.personalNumber2);
    }
  }

  private buildSubjectName(): string {
    const d = this.result.data;
    if (!d) { return ''; }
    if (d.opsubType === 'BSM') {
      if (d.name) { return d.name; }
      const n1 = [d.firstName, d.surname].filter(Boolean).join(' ');
      const n2 = [d.firstName2, d.surname2].filter(Boolean).join(' ');
      return [n1, n2].filter(Boolean).join(' a ');
    }
    if (d.name) { return d.name; }
    return [d.firstName, d.surname].filter(Boolean).join(' ');
  }
}
